/*
 * Small parts the Books tabs share: the category pick list, typed dollars, the span picker for reports, CSV downloads,
 * and opening a receipt.
 */
import { useState } from "react";
import { ACCOUNT_KIND_WORDS, REPORT_SPANS, spanOf, toCents, type CategoryRow, type ReportSpan } from "@steward/shared";
import { Choice, InputText } from "../../components/fields";
import { FilterPick } from "../../components/overlays";
import { money, shortDate } from "../../components/format";

export const KIND_WORDS: Record<string, string> = ACCOUNT_KIND_WORDS;
export const SOURCE_WORDS: Record<string, string> = {
  csv: "Bank file",
  ofx: "Bank file",
  plaid: "Linked bank",
  qb: "QuickBooks",
  typed: "Typed in",
};

/** Dollars typed by a person ("1,234.50", "-12") as cents, or null. */
export function centsOf(v: string): number | null {
  const s = v.replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  return toCents(s);
}

export function errText(e: unknown): string {
  return e instanceof Error ? e.message : "That didn't work.";
}

/** The categories a person can pick, main ones with their subcategories under them. */
export function pickList(categories: CategoryRow[]) {
  const shown = categories.filter((c) => !c.hidden && c.type !== "open");
  const out: { value: string; label: string; sub?: string }[] = [];
  for (const c of shown.filter((x) => !x.parentId)) {
    out.push({ value: c.id, label: c.name, sub: c.group });
    for (const s of shown.filter((x) => x.parentId === c.id))
      out.push({ value: s.id, label: `${c.name}, ${s.name}`, sub: c.group });
  }
  return out;
}

export function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: "up" | "down" | undefined;
}) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {hint ? <span className={`hint${tone ? ` ${tone}` : ""}`}>{hint}</span> : null}
    </div>
  );
}

const SPAN_WORDS: Record<ReportSpan | "custom", string> = {
  month: "This month",
  lastmonth: "Last month",
  quarter: "This quarter",
  lastquarter: "Last quarter",
  year: "This year",
  lastyear: "Last year",
  custom: "Pick days",
};

export interface Span {
  from: string;
  to: string;
}

/** A report's span: a named one (This month, Last year) or two days the owner picks. */
export function useSpan(today: string, start: ReportSpan = "year") {
  const [pick, setPick] = useState<ReportSpan | "custom">(start);
  const [custom, setCustom] = useState<Span>(spanOf(start, today));
  const span = pick === "custom" ? custom : spanOf(pick, today);
  return { pick, setPick, custom, setCustom, span };
}

export function SpanPicker({ s, today }: { s: ReturnType<typeof useSpan>; today: string }) {
  return (
    <div className="row tight bk-span">
      <FilterPick<ReportSpan | "custom">
        label="Days"
        value={s.pick}
        onChange={(v) => {
          if (v === "custom") s.setCustom(s.span);
          s.setPick(v);
        }}
        options={[...REPORT_SPANS, "custom" as const].map((v) => ({ value: v, label: SPAN_WORDS[v] }))}
      />
      {s.pick === "custom" ? (
        <>
          <InputText
            label="From"
            type="date"
            value={s.custom.from}
            max={s.custom.to}
            onChange={(e) => e.target.value && s.setCustom({ ...s.custom, from: e.target.value })}
          />
          <InputText
            label="To"
            type="date"
            value={s.custom.to}
            min={s.custom.from}
            onChange={(e) => e.target.value && s.setCustom({ ...s.custom, to: e.target.value })}
          />
        </>
      ) : (
        <span className="muted small">
          {shortDate(s.span.from, Number(today.slice(0, 4)))} to {shortDate(s.span.to, Number(today.slice(0, 4)))}
        </span>
      )}
    </div>
  );
}

/** Saves rows as a CSV file the accountant or a spreadsheet can open. */
export function downloadCsv(name: string, rows: (string | number | null)[][]) {
  const cell = (v: string | number | null) => {
    const s = v === null ? "" : String(v);
    // A cell starting with = + - @ is read as a formula by spreadsheets: a quote in front keeps it text.
    const safe = /^[=+\-@]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s) ? `'${s}` : s;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const text = rows.map((r) => r.map(cell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export const dollars = (c: number) => (c / 100).toFixed(2);

/** A file picked by a person, as base64 for the server. */
export function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result.replace(/^data:[^,]*,/, "") : "");
    r.onerror = () => reject(new Error("That file could not be read."));
    r.readAsDataURL(file);
  });
}

/** Opens a receipt in a new tab. */
export async function openReceipt(id: string, companyId: string) {
  if (__PREVIEW__) return;
  const w = window.open("", "_blank");
  const res = await fetch(`/v1/books/receipts/${id}`, { credentials: "same-origin", headers: { "x-company-id": companyId } });
  if (!res.ok) {
    w?.close();
    throw new Error("That receipt could not be opened.");
  }
  const url = URL.createObjectURL(await res.blob());
  if (w) w.location.href = url;
  else window.location.href = url;
}

/** Whole dollars for the big numbers on tiles: $13,271 not $13,271.20. */
export const whole = (c: number) => money(Math.round(c / 100) * 100);

/**
 * How far back Books goes (settings books.startDay): the first day in Books, or empty for everything the bank sends.
 * Lines before it leave Books (owner, 2026-11-02: "it should also ask how far to go, i already did my books").
 */
export function StartDayPick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const y = new Date().getFullYear();
  const thisYear = `${String(y)}-01-01`;
  const lastYear = `${String(y - 1)}-01-01`;
  const [picking, setPicking] = useState(value !== "" && value !== thisYear && value !== lastYear);
  const shown = picking ? "pick" : value === "" ? "all" : value;
  return (
    <div className="stack">
      <Choice
        label="How far back should Books go?"
        value={shown}
        onChange={(v) => {
          setPicking(v === "pick");
          onChange(v === "pick" ? value || thisYear : v === "all" ? "" : v);
        }}
        options={[
          {
            value: thisYear,
            label: `From Jan 1, ${String(y)}`,
            hint: `When ${String(y - 1)} and before are done in QuickBooks or by your tax pro`,
          },
          { value: lastYear, label: `From Jan 1, ${String(y - 1)}` },
          { value: "all", label: "Everything the bank sends", hint: "Most banks send up to 2 years" },
          { value: "pick", label: "Pick a day" },
        ]}
      />
      {picking ? (
        <InputText label="First day in Books" type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : null}
      <p className="muted small">Lines before that day stay out of Books. The opening balance comes from the bank.</p>
    </div>
  );
}
